import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { financialApiConfig } from '../config/api.config';
import { auth } from '../utils/firebase';

/**
 * Aggregated lifetime metrics for financial email campaigns
 */
export interface FinancialLifetimeMetrics {
  totalSent: number;
  totalDelivered: number;
  totalOpened: number;
  totalClicked: number;
  totalBounced: number;
  totalComplained: number;
  openRate: number;
  clickRate: number;
  deliveryRate: number;
  bounceRate: number;
  complaintRate: number;
}

const FINANCIAL_LIFETIME_METRICS_QUERY_KEY = ['financial-lifetime-metrics'];

/**
 * Helper to get Financial API URL
 * 
 * Constructs and normalizes the Financial API endpoint URL by:
 * - Validating that baseUrl is configured
 * - Removing trailing slashes from base URL
 * - Ensuring path starts with "/" for proper concatenation
 * 
 * @param {string} [path] - Optional API endpoint path (e.g., '/financial/tracking-data')
 * @returns {string} Complete normalized API URL
 * @throws {Error} Throws if Financial API Gateway URL is not configured in environment
 * 
 * @example
 * // Get base API URL
 * const baseUrl = getFinancialApiUrl();
 * // => 'https://api.example.com'
 * 
 * @example
 * // Get specific endpoint
 * const endpointUrl = getFinancialApiUrl('/financial/tracking-data');
 * // => 'https://api.example.com/financial/tracking-data'
 */
const getFinancialApiUrl = (path?: string): string => {
  const baseUrl = (financialApiConfig.baseUrl || '').trim();
  if (!baseUrl) {
    throw new Error('Financial API Gateway URL is not configured');
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '');
  if (!path) {
    return normalizedBase;
  }

  const trimmedPath = path.trim();
  const normalizedPath = trimmedPath.startsWith('/')
    ? trimmedPath
    : `/${trimmedPath}`;

  return `${normalizedBase}${normalizedPath}`;
};

/**
 * Helper to get Firebase authentication token
 * 
 * Retrieves the current user's Firebase ID token for API authorization.
 * Required for all authenticated API calls to the Financial API.
 * 
 * @async
 * @returns {Promise<string>} Firebase ID token for the authenticated user
 * @throws {Error} Throws if no user is currently authenticated
 * 
 * @example
 * try {
 *   const token = await getAuthTokenOrThrow();
 *   // Use token in Authorization header: `Bearer ${token}`
 * } catch (error) {
 *   console.error('Authentication failed:', error);
 * }
 */
const getAuthTokenOrThrow = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  return user.getIdToken();
};

/**
 * Fetches financial tracking data from the financial-campaign-tracking DynamoDB table
 * 
 * This function implements a rolling 4-month window query strategy to capture all active
 * email tracking records. Due to the 90-day TTL on DynamoDB items, querying the last 4 months
 * ensures we capture the complete dataset without gaps.
 * 
 * **Why Real Data is Needed:**
 * - Production dashboard relies on accurate lifetime metrics
 * - Real tracking data provides true delivery/engagement rates
 * - Mock data cannot reflect actual campaign performance
 * - Used for performance benchmarking and trend analysis
 * 
 * **Query Strategy:**
 * The Financial API Lambda requires month+year filters. Since SES tracking data is stored
 * with a 90-day TTL, we batch query the last 4 months (3 guaranteed + 1 buffer) to ensure
 * we don't miss any active records due to timing edge cases.
 * 
 * **Data Flow:**
 * 1. Fetch 4 months of data (current month rolling back)
 * 2. Merge all month responses
 * 3. Aggregate metrics across all items
 * 4. Calculate rates based on delivery events
 * 
 * @async
 * @returns {Promise<FinancialLifetimeMetrics>} Aggregated metrics object with totals and rates
 * @throws {Error} Throws if authentication fails; individual month failures are logged but don't block
 * 
 * @example
 * const metrics = await fetchFinancialTrackingData();
 * console.log(`Delivery Rate: ${(metrics.deliveryRate * 100).toFixed(2)}%`);
 * // => Delivery Rate: 98.45%
 */
const fetchFinancialTrackingData = async (): Promise<FinancialLifetimeMetrics> => {
  const token = await getAuthTokenOrThrow();
  const apiEndpoint = getFinancialApiUrl(
    financialApiConfig.trackingDataPath || '/financial/tracking-data'
  );

  const now = new Date();
  const allItems: any[] = [];

  // Month names as stored in DynamoDB (uppercase)
  const monthNames = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  // Query last 4 months to capture all data (90-day TTL means ~3 months of data)
  for (let monthsBack = 0; monthsBack < 4; monthsBack++) {
    const queryDate = new Date(now);
    queryDate.setMonth(queryDate.getMonth() - monthsBack);
    
    const month = monthNames[queryDate.getMonth()]; // Month name (JANUARY, FEBRUARY, etc.)
    const year = String(queryDate.getFullYear());

    try {
      /**
       * Pagination variables for handling large result sets
       * 
       * @type {string | undefined} nextToken - Token for fetching the next page of results
       * @type {boolean} hasMore - Flag indicating whether additional pages exist
       * 
       * The Financial API Lambda returns max 500 items per request. When hasMore is true,
       * use nextToken to fetch the subsequent page. This continues until all records are retrieved.
       * Critical for capturing all tracking data when campaigns have large recipient lists.
       */
      let nextToken: string | undefined = undefined;
      let hasMore = true;

      /**
       * Pagination loop: continues fetching until all pages for the month are retrieved
       * Each iteration fetches up to 500 items and checks for additional pages
       */
      while (hasMore) {
        // Fetch the next page of results using pagination token (undefined for first request)
        const apiResponse = await axios.get(apiEndpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            month,
            year,
            limit: 500, // Lambda enforces max limit of 500 records per request
            nextToken, // Include pagination token for subsequent requests (undefined for first)
          },
        });

        // Extract items from the response
        const items = (apiResponse?.data?.data?.items as any[]) ?? [];
        allItems.push(...items);

        // Extract pagination state from response and update loop variables
        hasMore = (apiResponse?.data?.data?.paging?.hasMore as boolean) ?? false;
        nextToken = (apiResponse?.data?.data?.paging?.nextToken as string | undefined);

        // Log pagination info for debugging large datasets
        if (nextToken) {
          console.debug(
            `Fetched ${items.length} items for ${year}-${month}, continuing pagination...`
          );
        }
      }
    } catch (error) {
      // Continue if a specific month fails (might not have data)
      console.warn(`Failed to fetch financial data for ${year}-${month}:`, error);
    }
  }

  const items = allItems;

  /**
   * AGGREGATION PHASE: Process all items and aggregate metrics
   * 
   * Strategy: Use Sets to track unique message IDs per event type rather than counters.
   * This prevents double-counting when a single message has multiple events.
   * 
   * Why Sets Instead of Counters:
   * - A message may appear in multiple records with different event types
   * - Multiple opens/clicks per message should count as one engagement
   * - Rate calculations require accurate unique message counts
   * - Sets provide O(1) lookup time for existence checks
   */

  /**
   * @type {number} totalSent - Total unique messages sent (aggregated from all records)
   * @type {number} totalDelivered - Total unique messages successfully delivered to recipient
   * @type {number} totalOpened - Total unique messages opened by recipients
   * @type {number} totalClicked - Total unique messages with links clicked
   * @type {number} totalBounced - Total unique messages bounced (hard/soft bounce)
   * @type {number} totalComplained - Total unique messages marked as spam/complaint
   * 
   * These are calculated from Set sizes after aggregation to ensure uniqueness
   */
  let totalSent = 0;
  let totalDelivered = 0;
  let totalOpened = 0;
  let totalClicked = 0;
  let totalBounced = 0;
  let totalComplained = 0;

  /**
   * Set-based tracking for unique message IDs by event type
   * 
   * @type {Set<string>} processedMessages - Tracks all unique messages seen for totalSent
   * @type {Set<string>} messagesWithDelivery - Messages with DELIVERY event
   * @type {Set<string>} messagesWithOpen - Messages with OPEN event
   * @type {Set<string>} messagesWithClick - Messages with CLICK event
   * @type {Set<string>} messagesWithBounce - Messages with BOUNCE event
   * @type {Set<string>} messagesWithComplaint - Messages with COMPLAINT event
   * @type {Set<string>} messagesWithDeliveryEvent - Intersection of delivery confirmations (used for engagement rate denominators)
   * 
   * Why Both Delivery Sets:
   * - messagesWithDelivery: for calculating delivery rate
   * - messagesWithDeliveryEvent: for filtering engagements to only delivered messages
   *   (prevents >100% engagement rates from old incomplete records)
   */
  const processedMessages = new Set<string>();
  
  const messagesWithDelivery = new Set<string>();
  const messagesWithOpen = new Set<string>();
  const messagesWithClick = new Set<string>();
  const messagesWithBounce = new Set<string>();
  const messagesWithComplaint = new Set<string>();
  
  const messagesWithDeliveryEvent = new Set<string>();

  /**
   * EMAIL FILTERING & AGGREGATION LOOP
   * 
   * Process each tracking record and categorize events by type.
   * This loop builds the Sets that will determine final metric counts.
   * 
   * @param {any} record - Financial tracking record from DynamoDB
   *   Contains: messageId, sesMessageId, eventId, events array, sesEventType
   * 
   * Algorithm:
   * 1. Extract messageId (try multiple fields for compatibility)
   * 2. Count unique messages for totalSent
   * 3. Process events array if available (preferred, contains full history)
   * 4. Fallback to sesEventType field if events array missing
   * 5. Add messageId to appropriate Set(s) based on event type
   */
  items.forEach((record: any) => {
    /**
     * Extract messageId from multiple possible fields for compatibility
     * Priority: messageId > sesMessageId > eventId
     */
    const messageId = record.messageId || record.sesMessageId || record.eventId;
    
    /**
     * Count sent emails: increment totalSent only once per unique messageId
     * This uses a Set to avoid double-counting when same message appears in multiple records
     */
    if (messageId && !processedMessages.has(messageId)) {
      processedMessages.add(messageId);
      totalSent += 1;
    }

    /**
     * Primary event processing path: handle events array
     * 
     * The events array contains the complete event history for a message:
     * [{ type: 'SEND', ... }, { type: 'DELIVERY', ... }, { type: 'OPEN', ... }]
     * 
     * Each event is processed independently to capture all engagement types.
     * SEND events are skipped here (already counted in totalSent above).
     */
    if (record.events && Array.isArray(record.events)) {
      /**
       * Process each historical event in the events array
       * 
       * Why process all events:
       * - Single message may have DELIVERY, OPEN, and CLICK events
       * - Need all event types to calculate complete engagement picture
       * - Open rate requires knowing which delivered messages were opened
       */
      record.events.forEach((event: any) => {
        const eventType = (event.type || event.sesEventType || '').toUpperCase();
        /**
         * Skip SEND events (already counted as unique messages)
         * Only process engagement and delivery outcome events
         */
        if (eventType !== 'SEND' && eventType !== 'FINANCIAL_SEND') {
          switch (eventType) {
            case 'DELIVERY':
            case 'DELIVERED':
              if (messageId) {
                messagesWithDelivery.add(messageId);
                messagesWithDeliveryEvent.add(messageId);
              }
              break;
            case 'OPEN':
              // Only count opens for messages that have delivery confirmation
              if (messageId) messagesWithOpen.add(messageId);
              break;
            case 'CLICK':
              // Only count clicks for messages that have delivery confirmation
              if (messageId) messagesWithClick.add(messageId);
              break;
            case 'BOUNCE':
              if (messageId) messagesWithBounce.add(messageId);
              break;
            case 'COMPLAINT':
              if (messageId) messagesWithComplaint.add(messageId);
              break;
          }
        }
      });
    } else {
      /**
       * Fallback event processing path: handle sesEventType field
       * 
       * Used when events array is not available (legacy records or API variations).
       * sesEventType represents the most recent event type for this message.
       * 
       * **Important:** This field may not capture all events for a message,
       * so it's only a fallback. Modern records should have events array.
       */
      const eventType = (record.sesEventType || record.eventType || '').toUpperCase();
      if (messageId) {
        /**
         * Process fallback event type the same way as array events
         */
        switch (eventType) {
          case 'DELIVERY':
          case 'DELIVERED':
            messagesWithDelivery.add(messageId);
            messagesWithDeliveryEvent.add(messageId);
            break;
          case 'OPEN':
            messagesWithOpen.add(messageId);
            break;
          case 'CLICK':
            messagesWithClick.add(messageId);
            break;
          case 'BOUNCE':
            messagesWithBounce.add(messageId);
            break;
          case 'COMPLAINT':
            messagesWithComplaint.add(messageId);
            break;
        }
      }
    }
  });

  /**
   * METRIC CONVERSION: Convert Sets to final counts
   * 
   * Each Set now contains unique message IDs per event type.
   * Convert to counts for easier calculations and API response.
   */
  totalDelivered = messagesWithDelivery.size;
  
  /**
   * Engagement metrics with delivery confirmation filter
   * 
   * **Critical filtering:** Only count engagements (opens, clicks, complaints) for messages
   * that have explicit DELIVERY confirmation. This prevents inflated engagement rates
   * from legacy records that may have engagement events but lack delivery proof.
   * 
   * **Example of problem this solves:**
   * - Old record: messageId '123' has OPEN event but no DELIVERY event
   * - Without filter: counts toward open rate, inflating engagement metrics
   * - With filter: excluded because no delivery confirmation
   * - Result: accurate engagement rates based only on confirmed deliveries
   */
  totalOpened = Array.from(messagesWithOpen).filter(id => messagesWithDeliveryEvent.has(id)).length;
  totalClicked = Array.from(messagesWithClick).filter(id => messagesWithDeliveryEvent.has(id)).length;
  totalBounced = messagesWithBounce.size;
  totalComplained = Array.from(messagesWithComplaint).filter(id => messagesWithDeliveryEvent.has(id)).length;

  /**
   * Tracking coverage metric: messages with any tracked event
   * 
   * Provides insight into tracking data completeness.
   * Used for debugging to identify if emails lack tracking setup.
   * 
   * @type {Set<string>} messagesWithAnyEvent - Union of all event type sets
   * @type {number} trackedMessageCount - Size of union (how many messages have any event)
   * 
   * Calculation: Union of all event types to get unique message count with at least one event.
   * If trackedMessageCount < totalSent, some messages have no tracking data.
   */
  const messagesWithAnyEvent = new Set();
  
  // Add items from each set without using spread operators (for TS compatibility)
  Array.from(messagesWithDeliveryEvent).forEach(id => messagesWithAnyEvent.add(id));
  Array.from(messagesWithBounce).forEach(id => messagesWithAnyEvent.add(id));
  Array.from(messagesWithOpen).forEach(id => messagesWithAnyEvent.add(id));
  Array.from(messagesWithClick).forEach(id => messagesWithAnyEvent.add(id));
  Array.from(messagesWithComplaint).forEach(id => messagesWithAnyEvent.add(id));
  
  const trackedMessageCount = messagesWithAnyEvent.size;

  /**
   * RATE CALCULATION: Convert totals to percentage rates
   * 
   * All rates are calculated with proper denominators to prevent division by zero
   * and to reflect realistic business metrics.
   */

  /**
   * Delivery rate: percent of emails with known outcome (delivered + bounced)
   * that were successfully delivered
   * 
   * Formula: totalDelivered / (totalDelivered + totalBounced)
   * 
   * Why include both delivered and bounced in denominator:
   * - These represent messages with known final status
   * - Excludes messages without tracking data
   * - Gives true success rate among tracked emails
   * - 0 if no messages have delivery/bounce tracking
   */
  const knownOutcomeCount = totalDelivered + totalBounced;
  const deliveryRate = knownOutcomeCount > 0 ? totalDelivered / knownOutcomeCount : 0;

  /**
   * Engagement rates: percent of delivered emails with engagement
   * 
   * @type {number} openRate - Percent of delivered emails that were opened
   * @type {number} clickRate - Percent of delivered emails with clicks
   * @type {number} complaintRate - Percent of delivered emails marked as spam
   * 
   * Formula: engagement / totalDelivered
   * 
   * Why use totalDelivered as denominator:
   * - Only delivered emails can be opened/clicked
   * - Bounce/complaint events are independent metrics
   * - 0 if totalDelivered is 0 (prevent NaN/Infinity)
   * 
   * Note: Can exceed 100% if data includes undelivered engagements (legacy issue)
   * - That's why we filter engagements to deliveryEvent only above
   */
  const openRate = totalDelivered > 0 ? totalOpened / totalDelivered : 0;
  const clickRate = totalDelivered > 0 ? totalClicked / totalDelivered : 0;

  /**
   * Bounce rate: percent of emails with known outcome that bounced
   * Formula: totalBounced / (totalDelivered + totalBounced)
   */
  const bounceRate = knownOutcomeCount > 0 ? totalBounced / knownOutcomeCount : 0;

  /**
   * Complaint rate: percent of delivered emails marked as spam/complaint
   * Formula: totalComplained / totalDelivered
   */
  const complaintRate = totalDelivered > 0 ? totalComplained / totalDelivered : 0;

  console.log('Financial metrics aggregation:', {
    totalSent,
    totalDelivered,
    totalOpened,
    totalClicked,
    totalBounced,
    totalComplained,
    trackedMessageCount,
    knownOutcomeCount,
    deliveryRate,
    openRate,
    clickRate,
  });

  return {
    totalSent,
    totalDelivered,
    totalOpened,
    totalClicked,
    totalBounced,
    totalComplained,
    deliveryRate,
    openRate,
    clickRate,
    bounceRate,
    complaintRate,
  };
};

/**
 * Custom hook that fetches and aggregates lifetime metrics for financial email campaigns.
 * Queries the /financial/tracking-data endpoint which reads from the
 * financial-campaign-tracking DynamoDB table.
 *
 * Uses @tanstack/react-query for data fetching with automatic refetching every 5 minutes.
 *
 * @returns Query result containing aggregated lifetime metrics and loading/error states
 */
export const useFinancialLifetimeMetrics = () => {
  return useQuery<FinancialLifetimeMetrics, Error>({
    queryKey: FINANCIAL_LIFETIME_METRICS_QUERY_KEY,
    queryFn: fetchFinancialTrackingData,
    staleTime: 30 * 1000, // 30 seconds (reduced for debugging from 4 minutes)
    gcTime: 30 * 60 * 1000, // 30 minutes (cache time)
    refetchInterval: 60 * 1000, // 1 minute (reduced for debugging from 5 minutes)
    refetchOnWindowFocus: true, // Enabled for debugging
    refetchOnReconnect: true, // Enabled for debugging
    retry: 2,
    retryDelay: (attempt) => Math.min(4000, 1000 * attempt),
    placeholderData: (previousData) => previousData,
  });
};

export type UseFinancialLifetimeMetricsResult = ReturnType<
  typeof useFinancialLifetimeMetrics
>;
